import { api } from "@/lib/axios";

export function startDirectDownload(url: string) {
  const parsed = new URL(url);
  if (parsed.protocol !== "https:") throw new Error("Invalid download URL");
  const anchor = document.createElement("a");
  anchor.href = parsed.href;
  anchor.rel = "noopener noreferrer";
  anchor.referrerPolicy = "no-referrer";
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
}

type DownloadOptions = {
  signal?: AbortSignal;
  method?: "get" | "post";
  data?: unknown;
};
type DownloadPayload = {
  download_url?: string;
  status_url?: string;
  status?: string;
};

const waitForExport = (signal?: AbortSignal) =>
  new Promise<void>((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException("Aborted", "AbortError"));
      return;
    }
    const abort = () => {
      clearTimeout(timer);
      reject(new DOMException("Aborted", "AbortError"));
    };
    const timer = setTimeout(() => {
      signal?.removeEventListener("abort", abort);
      resolve();
    }, 2000);
    signal?.addEventListener("abort", abort, { once: true });
  });

export async function getStoredDownloadUrl(
  endpoint: string,
  options: DownloadOptions = {}
): Promise<string> {
  const config = {
    params: { direct: 1 },
    headers: { Accept: "application/json" },
    signal: options.signal,
  };
  const { data } =
    options.method === "post"
      ? await api.post<DownloadPayload>(endpoint, options.data, config)
      : await api.get<DownloadPayload>(endpoint, config);
  if (data.download_url) return data.download_url;
  return waitForStoredDownload(data.status_url, options.signal);
}

export async function waitForStoredDownload(
  statusUrl: string | undefined,
  signal?: AbortSignal
): Promise<string> {
  const config = {
    params: { direct: 1 },
    headers: { Accept: "application/json" },
    signal,
  };
  if (!statusUrl || !/^\/exports\/files\/[0-9a-f-]+\/$/i.test(statusUrl)) {
    throw new Error("Direct download unavailable");
  }
  for (let attempt = 0; attempt < 900; attempt += 1) {
    await waitForExport(signal);
    const { data } = await api.get<DownloadPayload>(statusUrl, config);
    if (data.download_url) return data.download_url;
    if (data.status !== "pending" && data.status !== "processing")
      throw new Error("Export unavailable");
  }
  throw new Error("Export timed out; retry from export history");
}

export async function downloadStoredFile(
  endpoint: string,
  signal?: AbortSignal
) {
  startDirectDownload(await getStoredDownloadUrl(endpoint, { signal }));
}
